import { useEffect, useState } from "react";
import { RegisterCard } from "../../components/RegisterCard";
import { CircleLoader } from "../../components/CircleLoader";
import { Clock, AlignLeft, CheckCircle2, Calendar } from "lucide-react";

export const CAlogin = ({
  formData,
  onFormChange,
  onFormSubmit,
  loading,
}: any) => {
  const [formattedTime, setFormattedTime] = useState("");

  useEffect(() => {
    if (formData.time) {
      // Ensuring the value matches datetime-local format (YYYY-MM-DDTHH:mm)
      const formattedValue = formData.time.replace(" ", "T");
      setFormattedTime(formattedValue);
    }
  }, [formData.time]);

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value; // Keep T for the input itself
    setFormattedTime(value);
    onFormChange("time", value.replace("T", " ")); // Send back with space for backend
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-4 animate-in fade-in duration-700">
      {/* Header Section */}
      <div className="mb-8">
        <RegisterCard
          title="Log In Time"
          details="This activity timestamp will be instantly visible to parents and guardians."
        />
      </div>

      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-blue-900/5 overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          {/* Left Side: Form */}
          <form
            onSubmit={onFormSubmit}
            className="flex-1 p-8 sm:p-12 space-y-8 order-2 lg:order-1"
          >
            {/* Time Input Field */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-black text-slate-700 uppercase tracking-wider ml-1">
                <Clock size={16} className="text-blue-600" />
                Arrival Time
              </label>
              <div className="relative group">
                <input
                  type="datetime-local"
                  value={formattedTime}
                  onChange={handleTimeChange}
                  className="w-full h-14 px-5 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none transition-all duration-300 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 font-medium text-slate-700"
                  required
                />
              </div>
            </div>

            {/* Description Textarea */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-black text-slate-700 uppercase tracking-wider ml-1">
                <AlignLeft size={16} className="text-blue-600" />
                Notes / Description
              </label>
              <textarea
                name="description"
                value={formData["description"]}
                onChange={(e) => onFormChange("description", e.target.value)}
                placeholder="Add any specific notes about the arrival..."
                className="w-full p-5 bg-slate-50 border-2 border-slate-100 rounded-[1.5rem] min-h-[150px] outline-none transition-all duration-300 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-medium resize-none"
              />
            </div>

            {/* Submit Button */}
            <button
              disabled={loading}
              className={`group relative w-full h-16 rounded-2xl font-black text-lg transition-all duration-500 overflow-hidden shadow-xl shadow-blue-900/20
                ${
                  loading
                    ? "bg-slate-100 cursor-not-allowed"
                    : "bg-slate-900 text-white hover:bg-black hover:-translate-y-1 active:scale-95"
                }
              `}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

              <div className="flex items-center justify-center gap-3 relative z-10">
                {loading ? (
                  <CircleLoader />
                ) : (
                  <>
                    <CheckCircle2 size={22} className="text-blue-400" />
                    <span className="uppercase tracking-widest text-sm">
                      Save Entry
                    </span>
                  </>
                )}
              </div>
            </button>
          </form>

          {/* Right Side: Decorative/Illustration */}
          <div className="lg:w-80 bg-slate-50 border-l border-slate-100 flex flex-col items-center justify-center p-8 order-1 lg:order-2">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-500 blur-3xl opacity-10 rounded-full" />
              <img
                src="/img/log-in.svg"
                alt="login illustration"
                className="w-48 h-48 relative z-10 animate-pulse-slow"
              />
            </div>
            <div className="mt-8 text-center hidden lg:block">
              <p className="text-xs font-black text-slate-400 uppercase tracking-tighter">
                Security Protocol
              </p>
              <p className="text-sm font-medium text-slate-500 mt-1 italic">
                Timestamps are locked upon submission.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
